import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AlertTriangle, Bot, Copy, MoreHorizontal, Phone, User } from "lucide-react";
import { initialsFromLabel, normalizeBotExternalMatchUserIds } from "@/lib/messages/messages-helpers";
import { getAvatarColorClasses } from "@/lib/messages/avatar-color";
import PresenceDot from "@/components/presence/presence-dot";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

function copyToClipboard(text, successMessage, errorMessage) {
  const value = String(text || "").trim();
  if (!value) return;
  if (!navigator.clipboard?.writeText) {
    toast.error("Copy is not available in this environment.");
    return;
  }
  void navigator.clipboard.writeText(value).then(
    () => toast.success(successMessage),
    () => toast.error(errorMessage),
  );
}

/**
 * Read-only phone line, prefixed with a phone glyph so it aligns visually
 * with the subtitle line above (which also has a leading kind icon). The
 * copy action lives in the right-edge action cluster.
 */
function PhoneLine({ phoneLine, compact }) {
  return (
    <p
      className={cn(
        "m-0 flex min-w-0 max-w-full items-center gap-1 text-left tabular-nums text-muted-foreground",
        compact ? "mt-px text-[10px] leading-none" : "mt-0.5 text-xs leading-snug",
      )}
    >
      <Phone
        className={cn(compact ? "size-2.5" : "size-3", "shrink-0")}
        strokeWidth={2}
        aria-hidden
      />
      <span className="min-w-0 truncate">{phoneLine}</span>
    </p>
  );
}

/**
 * Right-edge action cluster: copy-phone (when present) + open-details (kebab).
 * Uses span+role buttons so the cluster works while nested inside a parent <button>.
 */
function RowActions({ row, compact, stopParentClick, onOpenDetails }) {
  const consume = (e) => {
    if (stopParentClick) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleCopyPhone = (e) => {
    consume(e);
    copyToClipboard(row.phoneLine, "Phone number copied", "Could not copy phone number");
  };

  const handleOpenDetails = (e) => {
    consume(e);
    onOpenDetails?.(row);
  };

  const handleKey = (action) => (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      action(e);
    }
  };

  const buttonBase =
    "inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";
  const sizeClass = compact ? "h-5 w-5" : "h-7 w-7";
  const iconSize = compact ? "size-3" : "size-3.5";

  if (!row.phoneLine && !onOpenDetails) return null;

  return (
    <div className={cn("flex shrink-0 items-center gap-0.5", compact ? "self-center" : "self-start mt-0.5")}>
      {row.phoneLine ? (
        <span
          role="button"
          tabIndex={0}
          aria-label="Copy phone number"
          className={cn(buttonBase, sizeClass)}
          onClick={handleCopyPhone}
          onKeyDown={handleKey(handleCopyPhone)}
        >
          <Copy className={iconSize} strokeWidth={2} aria-hidden />
        </span>
      ) : null}
      {onOpenDetails ? (
        <span
          role="button"
          tabIndex={0}
          aria-label="Open user details"
          className={cn(buttonBase, sizeClass)}
          onClick={handleOpenDetails}
          onKeyDown={handleKey(handleOpenDetails)}
        >
          <MoreHorizontal className={iconSize} strokeWidth={2} aria-hidden />
        </span>
      ) : null}
    </div>
  );
}

function memberKindIcon(row, compact) {
  if (row.isBot) {
    return <Bot className={cn(compact ? "size-2.5" : "size-3", "shrink-0")} strokeWidth={2} aria-hidden />;
  }
  return <User className={cn(compact ? "size-2.5" : "size-3", "shrink-0")} strokeWidth={2} aria-hidden />;
}

/**
 * @param {object} props
 * @param {object} props.row
 * @param {boolean} props.compact
 * @param {boolean} [props.adminDirectClickable]
 * @param {(row: object) => void} [props.onMemberUserClick] — opens user details (direct + team); not used for bots
 * @param {{ online: boolean, availability?: string } | null} [props.presence] — internal Chats presence dot on the avatar
 */
export function ThreadMemberRow({ row, compact, adminDirectClickable = false, onMemberUserClick, presence = null }) {
  const isTeamMember = String(row.memberSource || "").toLowerCase() === "team";
  const ambiguous = Boolean(adminDirectClickable && !row.isBot && row.ambiguousDuplicate);
  const isInsideButton = (adminDirectClickable && !row.isBot) || (onMemberUserClick && !row.isBot);
  const hasPhone = Boolean(row.phoneLine);
  const canOpenDetails = Boolean(onMemberUserClick && !row.isBot);

  // Deterministic colored avatar background for non-bot, non-self rows so different
  // people read distinctly at a glance (matches the thread list pattern).
  const fallbackColorClass = row.isBot
    ? "bg-emerald-500/15 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
    : row.isSelf
      ? "bg-primary/15 text-primary"
      : getAvatarColorClasses(row.label || row.id);

  const baseShell = "group relative flex rounded-lg transition-colors";

  const shellClass = cn(
    baseShell,
    compact
      ? cn(
          "items-center gap-2 px-2 py-1.5",
          "border border-transparent hover:border-border/50 hover:bg-muted/40",
          hasPhone ? "items-start" : "",
        )
      : ambiguous
        ? [
            "items-start gap-3 px-3 py-2.5",
            "border border-amber-500/55 bg-amber-500/10 shadow-sm ring-2 ring-amber-400/45 dark:bg-amber-500/10",
            "transition-[background-color,border-color,box-shadow,ring-color] duration-200 ease-out",
            isInsideButton
              ? cn(
                  "w-full cursor-pointer text-left",
                  "hover:border-amber-500/80 hover:bg-amber-300/30 hover:shadow-md hover:ring-amber-500/55",
                  "dark:hover:border-amber-400/90 dark:hover:bg-amber-400/12 dark:hover:ring-amber-400/50",
                  "active:bg-amber-200/40 dark:active:bg-amber-500/15",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                )
              : "",
          ]
        : [
            hasPhone ? "items-start gap-3" : "items-center gap-3",
            "border border-border/50 bg-background/60 px-3 py-2.5",
            isTeamMember && isInsideButton
              ? cn(
                  "w-full cursor-pointer text-left",
                  "hover:border-sky-500/35 hover:bg-sky-500/5",
                  "active:bg-sky-500/10",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                )
              : isInsideButton
                ? cn(
                    "w-full cursor-pointer text-left",
                    "hover:border-primary/40 hover:bg-primary/5",
                    "active:bg-primary/10",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                  )
                : "",
          ],
  );

  const avatar = (
    <span className={cn("relative shrink-0", hasPhone && "mt-0.5")}>
      <Avatar className={cn(compact ? "h-6 w-6" : "h-9 w-9")}>
        {!row.isBot && row.avatarUrl ? <AvatarImage src={row.avatarUrl} alt={row.label} /> : null}
        <AvatarFallback className={cn(compact ? "text-[9px]" : "text-[11px]", "font-semibold", fallbackColorClass)}>
          {initialsFromLabel(row.label)}
        </AvatarFallback>
      </Avatar>
      {presence && !row.isBot ? (
        <PresenceDot
          online={presence.online}
          availability={presence.availability}
          className="absolute -bottom-0.5 -right-0.5"
        />
      ) : null}
    </span>
  );

  const nameAndMeta = (
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-1.5">
        <p
          className={cn(
            "min-w-0 truncate text-foreground",
            compact ? "text-[11px] font-medium leading-tight" : "text-sm font-semibold",
          )}
          title={row.label}
        >
          {row.label}
        </p>
        {row.isSelf ? (
          <span
            className={cn(
              "shrink-0 rounded bg-primary/15 px-1 text-[9px] font-bold uppercase tracking-wide text-primary",
              compact ? "py-0" : "py-px",
            )}
          >
            You
          </span>
        ) : null}
      </div>
      <p
        className={cn(
          "flex items-center gap-1 truncate text-muted-foreground",
          compact ? "text-[10px] leading-tight" : "text-xs",
        )}
      >
        {memberKindIcon(row, compact)}
        <span className="truncate">{row.subtitle}</span>
      </p>
      {row.phoneLine ? <PhoneLine phoneLine={row.phoneLine} compact={compact} /> : null}
      {ambiguous ? (
        <p className="mt-2 border-t border-amber-500/30 pt-2 text-[11px] font-medium leading-snug text-amber-900/90 dark:text-amber-200/95">
          Other users share this number. Pick the correct account for migration.
        </p>
      ) : null}
    </div>
  );

  const trailing = (
    <>
      {ambiguous ? (
        <AlertTriangle
          className={cn(
            "pointer-events-none shrink-0 text-amber-600 transition-colors dark:text-amber-500",
            compact ? "h-3.5 w-3.5" : "mt-0.5 h-5 w-5",
          )}
          strokeWidth={2}
          aria-hidden
        />
      ) : null}
      <RowActions
        row={row}
        compact={compact}
        stopParentClick={isInsideButton}
        onOpenDetails={canOpenDetails && !isInsideButton ? onMemberUserClick : undefined}
      />
    </>
  );

  const inner = (
    <>
      {avatar}
      {nameAndMeta}
      {trailing}
    </>
  );

  if (adminDirectClickable && !row.isBot) {
    return (
      <li>
        <button
          type="button"
          className={shellClass}
          aria-label={
            ambiguous
              ? normalizeBotExternalMatchUserIds(row.adminUserSnapshot?.metadata).length > 0
                ? `Duplicate number: ${row.label}. Open to compare accounts and choose migration target.`
                : `Duplicate number warning: ${row.label}. Review user details.`
              : `Open user details for ${row.label}`
          }
          onClick={() => onMemberUserClick?.(row)}
        >
          {inner}
        </button>
      </li>
    );
  }

  if (onMemberUserClick && !row.isBot) {
    return (
      <li>
        <button
          type="button"
          className={shellClass}
          aria-label={`Open user details for ${row.label}`}
          onClick={() => onMemberUserClick(row)}
        >
          {inner}
        </button>
      </li>
    );
  }

  return <li className={shellClass}>{inner}</li>;
}
